import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { departmentLabelKey, ticketStatusLabelKey } from '@nbh/shared';
import type { TicketAuditEntry, TicketStatus } from '@nbh/shared';
import { formatDateTime } from '../../lib/format';
import { LIFECYCLE_ACTIONS, actionPhrase, actorName } from './auditPhrase';

/** How many rows a page shows. Enough to cover a job's whole life without a scroll war. */
const PAGE_SIZE = 20;


type TrailFilter = 'lifecycle' | 'all';

export interface TicketAuditTrailProps {
  entries: TicketAuditEntry[];
}

/**
 * The trail, chronologically.
 *
 * Rewritten from a list that rendered `action`, `toStatus`, `reason` and a timestamp and
 * **never said who did anything** — the server held `actorId` on every row and the
 * endpoint did not join it, so the one question a history is opened to answer was the one
 * it could not. Now every row names its actor, shows the move it made, and carries its
 * reason.
 *
 * Paged rather than scrolled and capped rather than unbounded: prior rejections must stay
 * visible however many there are (R9), but a job rejected a dozen times must not hand a
 * five-year-old Android a list it renders one row at a time.
 */
export function TicketAuditTrail({ entries }: TicketAuditTrailProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<TrailFilter>('lifecycle');
  const [shown, setShown] = useState(PAGE_SIZE);

  const visible = useMemo(
    () =>
      filter === 'all' ? entries : entries.filter((entry) => LIFECYCLE_ACTIONS.has(entry.action)),
    [entries, filter],
  );

  if (entries.length === 0) {
    return <p className="text-sm text-slate-500">{t('detail.noAudit')}</p>;
  }

  const page = visible.slice(0, shown);

  return (
    <div>
      <div className="mb-3 flex gap-2" role="group" aria-label={t('audit.filterLabel')}>
        {(['lifecycle', 'all'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={filter === option}
            onClick={() => {
              setFilter(option);
              // Widening the filter should not also drop the reader back to page one of
              // a longer list; narrowing it should not leave a stale deep page.
              setShown(PAGE_SIZE);
            }}
            className={`min-h-[44px] rounded-full px-4 text-sm font-medium ${
              filter === option ? 'bg-slate-200 text-slate-900' : 'bg-slate-800 text-slate-300'
            }`}
          >
            {t(`audit.filter.${option}`)}
          </button>
        ))}
      </div>

      <ol className="space-y-3">
        {page.map((entry) => (
          <TrailRow key={entry.id} entry={entry} />
        ))}
      </ol>

      {shown < visible.length && (
        <button
          type="button"
          onClick={() => setShown((count) => count + PAGE_SIZE)}
          className="mt-3 min-h-[48px] w-full rounded-xl bg-slate-800 px-4 text-base font-semibold text-slate-100"
        >
          {t('audit.showMore', { count: visible.length - shown })}
        </button>
      )}
    </div>
  );
}

function TrailRow({ entry }: { entry: TicketAuditEntry }) {
  const { t } = useTranslation();

  return (
    <li
      className={`border-l-2 pl-3 text-sm ${
        // Self-verification is permitted but must never pass unnoticed by whoever reads
        // the trail later, so it is marked on the row itself, not only in a chip.
        entry.isSelfVerified ? 'border-amber-400' : 'border-slate-700'
      }`}
    >
      <p className="text-slate-100">
        <span className="font-medium">{actorName(entry, t)}</span> {actionPhrase(entry, t)}
      </p>

      {entry.fromStatus && entry.toStatus && (
        <p className="text-xs text-slate-400">
          {t(ticketStatusLabelKey[entry.fromStatus as TicketStatus])} →{' '}
          {t(ticketStatusLabelKey[entry.toStatus as TicketStatus])}
        </p>
      )}

      {/* The queue move a cross-department reassignment carries with it (D38). */}
      {entry.fromDepartment && entry.toDepartment && (
        <p className="text-xs text-slate-400">
          {t(departmentLabelKey[entry.fromDepartment])} →{' '}
          {t(departmentLabelKey[entry.toDepartment])}
        </p>
      )}

      {entry.isSelfVerified && (
        <p className="mt-1 inline-block rounded bg-amber-500/20 px-2 py-0.5 text-xs font-semibold text-amber-300">
          {t('audit.selfVerified')}
        </p>
      )}

      {entry.reason && <p className="mt-1 text-xs italic text-slate-300">“{entry.reason}”</p>}

      <p className="text-xs text-slate-600">{formatDateTime(entry.createdAt)}</p>
    </li>
  );
}
